import React from 'react';
import * as PropTypes from 'prop-types';
import { get, omit } from 'lodash';

import AssessmentsTableRow from 'chameleon/ui-stack/ui/assessments/AssessmentsTableRow';
import styles from 'chameleon/ui-stack/ui/assessments/styles.css';
import CustomCell from 'chameleon/ui-stack/ui/assessments/CustomCell';
import AssignmentTypeIcon from 'chameleon/ui-stack/ui/assessments/assignment-type-icon';
import {
  SPP,
  TEACHER_AUTHORED,
} from 'chameleon-src/components/assignments/constants';
import TextWithTooltip from 'chameleon/ui-stack/ui/assessments/text-with-tooltip';
import RetiredItemIcon from 'legacy-src/utility/IconWithDescription/RetiredItemIcon';
import { SecureInfoIcon } from 'legacy-src/utility';
import LabelList from 'chameleon/ui-stack/ui/assessments/label-list';
import dateTimeFormat from 'chameleon/ui-stack/utilities/date';
import Actions from 'chameleon/ui-stack/ui/assessments/actions';
import Columns from 'chameleon/ui-stack/ui/base/table/Columns';
import BulkTableCheckbox from 'legacy-src/components/TeacherOrganize/components/BulkTableCheckbox';
import QuizItemControls from 'chameleon/ui-stack/ui/tables/QuizItemControls';

import SummaryRowStatus from './SummaryRowStatus';
import { getAssessmentLabels } from './util';
import { noDataPlaceholder } from '../util';
import SignedLink from '../../units/subunit-content/SignedLink';
import { resourceTypes } from 'chameleon/legacy/src/components/TeacherOrganize/utils/constants';

export const SummaryRow = (props) => {
  const {
    assessment = {},
    row = {},
    sharedQuizIcon,
    isVideo,
    addColumnPadding,
    unassignedDateAccessor,
    moveItem = () => {},
    onLabelRemoved = () => {},
    isLast = false,
    showReorderActions = false,
    videoResourcesByResourceId,
    myAssignmentsPdfFF,
    labelsDnDFF,
    onResourceLabelUpdate,
  } = props;
  const { original = {}, id } = row || {};
  const { type } = original;

  const { assessmentLabels, updateLabelAssociations } = getAssessmentLabels(
    props.subjectInfo,
    assessment.labels,
  );

  const ariaLabel = assessment.title || '';

  const CellPadding = ({ name }) =>
    CustomCell(
      `${row.id}-${name}-padding`,
      '',
      `${styles.light_cell}, ${styles.icon} ${styles.table_cell}`,
    );
  const isPDF = assessment.type === resourceTypes.pdf;
  const {
    contains_secure_questions: containsSecureQuestions,
    is_practice_exam: isPracticeExam,
  } = assessment;

  return (
    <AssessmentsTableRow
      row={row}
      rowId={row.original.id}
      labelsDnDFF={labelsDnDFF}
      className="border-gray-400"
      aria-label={assessment.title}
    >
      <Columns
        className={styles.table_columns_chunky}
        cells={[
          CustomCell(
            `${id}-title`,
            <div className="flex items-center font-medium">
              <BulkTableCheckbox
                id={assessment.id}
                title={assessment.title}
                type={assessment.type}
              />
              {showReorderActions && (
                <div className="pr-3">
                  <QuizItemControls
                    moveItem={moveItem}
                    index={row.index}
                    isLast={isLast}
                  />
                </div>
              )}
              {sharedQuizIcon || (
                <AssignmentTypeIcon
                  height="36"
                  width="36"
                  type={get(original, 'is_spp') ? SPP : assessment.type}
                />
              )}
              <span className="ml-4 pr-4">
                {isPDF ? (
                  <SignedLink
                    isSigned={false}
                    aria-label={ariaLabel}
                    url={assessment.url}
                    className="text-black hover:underline"
                  >
                    {ariaLabel}
                  </SignedLink>
                ) : (
                  <TextWithTooltip
                    tooltip="Preview Resource"
                    ariaLabel={props.titleAriaLabel}
                    toolTipElementProps={{
                      role: 'link',
                      'data-drag-element': true,
                    }}
                    onClick={props.onClick}
                  >
                    {ariaLabel}
                  </TextWithTooltip>
                )}
              </span>
              {assessment.contains_retired_questions && (
                <div>
                  <RetiredItemIcon isQuestion={false} />
                </div>
              )}
              {!!containsSecureQuestions && !isPracticeExam && (
                <div>
                  <SecureInfoIcon isQuestion={false} />
                </div>
              )}
            </div>,
            `${styles.table_cell} ${styles.title}`,
          ),
          CustomCell(
            `${id}-labels}`,
            <LabelList
              labels={assessmentLabels}
              type={type}
              assessmentId={assessment?.id}
              assessmentTitle={assessment?.title}
              updateLabelAssociations={updateLabelAssociations}
              onLabelRemoved={onLabelRemoved}
            />,
            styles.table_cell,
          ),
          /**
           * If we are showing an unassigned row when we are not under the Unassigned filter
           * and we have a label filter, then we need to "pad" the table so that an
           * Unassigned row (with less columns) fits into a table made for Assignments
           *
           * Start Date column padding
           */
          ...(addColumnPadding ? [CellPadding({ name: 'start-date' })] : []),
          /**
           * If we are showing an unassigned row when we are not under the Unassigned filter
           * and we have a label filter, then we need to "pad" the table so that an
           * Unassigned row (with less columns) fits into a table made for Assignments
           *
           * End Date column padding
           */
          ...(addColumnPadding ? [CellPadding({ name: 'end-date' })] : []),

          ...(!addColumnPadding
            ? [
                CustomCell(
                  `${row.id}-${3}`,
                  assessment.type === TEACHER_AUTHORED
                    ? dateTimeFormat(
                        assessment[unassignedDateAccessor],
                        null,
                        'dateStandard',
                      )
                    : noDataPlaceholder,
                  `${styles.light_cell} ${styles.table_cell}`,
                ),
              ]
            : []),
          CustomCell(
            `${id}-status`,
            <SummaryRowStatus
              {...props}
              isVideo={isVideo}
              assessment={assessment}
            />,
            `${styles.light_cell}, ${styles.icon} ${styles.table_cell}`,
          ),
          /**
           * If we are showing an unassigned row when we are not under the Unassigned filter
           * and we have a label filter, then we need to "pad" the table so that an
           * Unassigned row (with less columns) fits into a table made for Assignments
           *
           * Results column padding
           */
          ...(addColumnPadding ? [CellPadding({ name: 'results' })] : []),
          ...(assessment.visibility_to_students !== 'AlwaysShow' ||
          (isPDF && assessment.actions.includes('share'))
            ? [
                CustomCell(
                  `${id}-actions`,
                  <Actions
                    assignment={{
                      assessment: omit(assessment, ['assignments']),
                    }}
                    type={assessment.type}
                    basePathname={props.basePathname}
                    fetchAssessmentData={props.fetchAssessmentData}
                    subjectInitId={props.subjectInitId}
                    isFinalExam={assessment.isFinalExam}
                    isPilot={assessment.isPilot}
                    isSPP={assessment.is_spp}
                    isPDF={isPDF}
                    section={assessment.section}
                    isVideo={isVideo}
                    subjectInfo={props.subjectInfo}
                    assessment={assessment}
                    isAllResourcesPage
                    sendTelemetryEvent={props.sendTelemetryEvent}
                    closeQuizReturnPath={props.closeQuizReturnPath}
                    assessmentPreviewURL={
                      props.previewBaseUrl
                        ? `${props.previewBaseUrl}&quizId=${assessment.id}`
                        : null
                    }
                    shareAssessmentInviteItemId={
                      props.sharedAssessmentsFF ? assessment.id : null
                    }
                    shareKey={original.shareKey}
                    isMyAssignmentsPage
                    videoResourcesByResourceId={videoResourcesByResourceId}
                    disabled={isPDF && !myAssignmentsPdfFF}
                    onResourceLabelUpdate={onResourceLabelUpdate}
                  />,
                ),
              ]
            : []),
        ]}
      />
    </AssessmentsTableRow>
  );
};

SummaryRow.propTypes = {
  row: PropTypes.object,
  titleAriaLabel: PropTypes.string,
  sharedQuizIcon: PropTypes.string,
  assessment: PropTypes.object,
  onClick: PropTypes.func,
  unassignedDateAccessor: PropTypes.string,
  isVideo: PropTypes.bool,
  basePathname: PropTypes.string,
  fetchAssessmentData: PropTypes.func,
  subjectInitId: PropTypes.string,
  subjectInfo: PropTypes.object,
  sendTelemetryEvent: PropTypes.func,
  closeQuizReturnPath: PropTypes.string,
  previewBaseUrl: PropTypes.string,
  sharedAssessmentsFF: PropTypes.bool,
};
